/**
 * The single boundary that normalizes the `/api/users/personal-info/base/`
 * payload into the clean shape the resolver consumes.
 *
 * Verified against the backend's OpenAPI schema (FGR `__types__/api`,
 * `components.schemas` PersonalBaseInfo / UserRole / PermissionsEnum), the real
 * response already provides exactly what the resolver needs:
 *   - roles:       open boolean flag map (is_company_admin, is_teamlead, …) — `RoleFlags`
 *   - permissions: snake_case CODES (deal_management, application_processing, …)
 *   - types:       string[]
 * so this adapter is a verbatim pass-through with null-handling (absent flags
 * read as undefined -> falsy; no coercion, no enumeration). There is deliberately
 * NO role->code bridge and NO label->code mapping — those would only be needed
 * for a pre-Phase-0 payload this backend does not send, and a bridge would now
 * DOUBLE-GRANT on top of the real codes.
 *
 * NOTE: we read the real fields structurally via `ProfileLike` and never import
 * `@tenant/core` — keeping `capabilities` a leaf lib (no core<->capabilities
 * cycle). Core's `ProfileType` is a deliberately CLOSED view of the same flags
 * (typo-safety for direct reads); this lib's `RoleFlags` is the open view the
 * registry resolves against.
 */

/**
 * An open boolean role-flag map (`is_company_admin`, `is_teamlead`, …). Deliberately
 * NOT enumerated: the ONLY place roles are listed is the generated `ReqRole` union.
 * That keeps this lib role-agnostic — a new backend role (added to the CSV) flows
 * through with no change here.
 */
export type RoleFlags = { readonly [flag: string]: boolean | undefined };

/** The subset of the real payload the adapter reads. `Profile` satisfies this structurally. */
export interface ProfileLike {
  types?: string[];
  roles?: RoleFlags;
  permissions?: string[];
}

/** The exact shape `resolver.ts` consumes. `permissions` are snake_case CODES. */
export interface CapabilityUser {
  types: string[];
  roles: RoleFlags;
  permissions: string[];
}

export function toCapabilityUser(profile: ProfileLike | null | undefined): CapabilityUser | null {
  if (!profile) {
    return null;
  }
  return {
    types: profile.types ?? [],
    // Pass ALL role flags through untouched. The resolver reads them by name
    // (`is_${reqRole}`), so any role the backend sends and the registry references
    // resolves with no change here — adding a role is a CSV edit, not an adapter edit.
    roles: { ...(profile.roles ?? {}) },
    permissions: profile.permissions ?? [],
  };
}
